import { supabase } from "@/lib/supabase";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const STORAGE_KEY = "file-storage";

const SUPABASE_BUCKETS = [
  "chat", // paths: images, files
  "avatars", // paths: user, assistant, workspace
];

const INITIAL_STATE = {
  // Store state
  isLoading: false,
  error: null,
  initialized: false,
  initializing: false,
  // State
  defaultBucketNames: SUPABASE_BUCKETS,
  bucketMap: new Map(
    SUPABASE_BUCKETS?.map((bucket) => [bucket, { name: bucket, files: [] }])
  ),
  buckets: SUPABASE_BUCKETS.map((name) => ({ name, files: [] })),
  currentBucket: null,
  currentBucketName: "",
  currentBucketFiles: [],
  // Track currently uploaded files with complete metadata
  currentFiles: [],
  assistantImages: [],
  status: "idle",
};

// State validation function to ensure critical arrays are always defined
const validateState = (state) => {
  const validatedState = { ...state };

  // Ensure critical arrays are always defined
  if (!Array.isArray(validatedState.currentBucketFiles)) {
    console.warn("currentBucketFiles was not an array, resetting to []");
    validatedState.currentBucketFiles = [];
  }

  if (!Array.isArray(validatedState.currentFiles)) {
    console.warn("currentFiles was not an array, resetting to []");
    validatedState.currentFiles = [];
  }

  if (!Array.isArray(validatedState.assistantImages)) {
    console.warn("assistantImages was not an array, resetting to []");
    validatedState.assistantImages = [];
  }

  // Ensure Maps are properly initialized
  if (!(validatedState.bucketMap instanceof Map)) {
    console.warn("bucketMap was not a Map, reinitializing");
    validatedState.bucketMap = new Map(
      SUPABASE_BUCKETS?.map((bucket) => [bucket, { name: bucket, files: [] }])
    );
  }

  return validatedState;
};

export const useFileStorageStore = create(
  persist((set, get) => ({
    ...validateState(INITIAL_STATE),
    /* ----------------------- */
    /* ---- INITIALIZER ------ */
    /* ----------------------- */
    initializeFileStorageStore: async (userId) => {
      set({ initializing: true });

      try {
        // Initialize buckets with predefined names - no validation needed
        set({
          buckets: SUPABASE_BUCKETS.map((name) => ({ name, files: [] })),
          bucketMap: new Map(
            SUPABASE_BUCKETS.map((bucket) => [
              bucket,
              { name: bucket, files: [] },
            ])
          ),
        });

        // Try to check storage for the chat bucket, but don't fail if it has issues
        try {
          await get().checkStorage("chat");
        } catch (storageError) {
          console.warn(
            "Error checking storage, but proceeding with initialization:",
            storageError
          );
          // Non-fatal, continue with initialization
        }

        // Mark as initialized
        set({ initialized: true });

        return { success: true };
      } catch (error) {
        console.error("Error initializing file storage:", error);
        set({
          error: error.message || "Failed to initialize storage",
          initialized: true, // Still mark as initialized to prevent blocking the app
        });
        return { success: false, error };
      } finally {
        set({ initializing: false });
      }
    },

    /* ----------------------- */
    /* ----- FILE TRACKING --- */
    /* ----------------------- */
    addCurrentFile: (fileData) => {
      if (!fileData) return;

      // Generate a valid ID if none exists
      const fileId =
        fileData.id ||
        fileData.openai_file_id ||
        `file-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

      // Create normalized file object with required fields
      const normalizedFile = {
        ...fileData,
        id: fileId,
        openai_file_id: fileData.openai_file_id || fileData.id || fileId,
        name: fileData.name || "Unnamed File",
        type: fileData.type || "application/octet-stream",
        size: fileData.size || 0,
        path: fileData.path || fileData.storage_path,
        storage_path: fileData.storage_path || fileData.path,
        added_at: new Date().toISOString(),
        // Meta flags for tool usage
        isImage:
          fileData.isImage || fileData.type?.startsWith("image/") || false,
        isDocument:
          fileData.isDocument ||
          fileData.type?.includes("pdf") ||
          fileData.type?.includes("document") ||
          fileData.type?.includes("text/") ||
          false,
      };

      set((state) => {
        // Check if this file already exists by ID
        const existingFileIndex = state.currentFiles.findIndex(
          (f) =>
            f.id === normalizedFile.id ||
            f.openai_file_id === normalizedFile.openai_file_id
        );

        // Update if exists, add if not
        if (existingFileIndex >= 0) {
          const updatedFiles = [...state.currentFiles];
          updatedFiles[existingFileIndex] = {
            ...updatedFiles[existingFileIndex],
            ...normalizedFile,
            updated_at: new Date().toISOString(),
          };
          return { currentFiles: updatedFiles };
        } else {
          return {
            currentFiles: [...state.currentFiles, normalizedFile],
          };
        }
      });
    },

    clearCurrentFiles: () => {
      set({ currentFiles: [] });
    },

    /* ----------------------- */
    /* ---- STORAGE UTILS ---- */
    /* ----------------------- */
    checkStorage: async (bucketName = "chat", userIdFilter = null) => {
      set({ isLoading: true, error: null });
      try {
        // Get user ID from auth store if not provided
        if (!userIdFilter) {
          const { useAuthStore } = await import("@/store/authStore");
          userIdFilter =
            useAuthStore.getState().userId || useAuthStore.getState().user?.id;
        }

        // Try to list the bucket contents directly
        // If we have a user ID, we can optionally filter by user directory
        const listPath = userIdFilter ? `${userIdFilter}/` : "";
        const { data, error } = await supabase.storage
          .from(bucketName)
          .list(listPath);

        // Handle potential error from listing but don't crash
        if (error) {
          console.warn(
            `Error listing bucket "${bucketName}" at path "${listPath}":`,
            error
          );
          set({
            isLoading: false,
            currentBucket: { name: bucketName, files: [] },
            currentBucketName: bucketName,
            currentBucketFiles: [],
          });
          return [];
        }

        // Process files to add full paths and URLs
        const processedFiles = await Promise.all(
          (data || []).map(async (item) => {
            try {
              const fullPath = listPath ? `${listPath}${item.name}` : item.name;

              // If it's a file (has an ID), get its URL
              if (item.id) {
                const { data: urlData } = await supabase.storage
                  .from(bucketName)
                  .getPublicUrl(fullPath);

                return {
                  ...item,
                  fullPath,
                  publicUrl: urlData?.publicUrl,
                  type: "file",
                  user_id: userIdFilter, // Add user context
                };
              }
              // Otherwise it's a folder
              else {
                return {
                  name: item.name,
                  fullPath,
                  type: "folder",
                  user_id: userIdFilter, // Add user context
                };
              }
            } catch (itemErr) {
              console.warn(`Error processing storage item:`, itemErr);
              // Return a minimal version of the item if there's an error
              return {
                name: item.name || "unknown",
                fullPath: item.name || "unknown",
                type: item.id ? "file" : "folder",
                error: itemErr.message,
                user_id: userIdFilter,
              };
            }
          })
        );

        // Update bucket in bucketMap
        const updatedBucketMap = new Map(get().bucketMap);
        updatedBucketMap.set(bucketName, {
          name: bucketName,
          files: processedFiles,
          user_id: userIdFilter,
        });

        set({
          bucketMap: updatedBucketMap,
          currentBucket: {
            name: bucketName,
            files: processedFiles,
            user_id: userIdFilter,
          },
          currentBucketName: bucketName,
          currentBucketFiles: processedFiles,
          isLoading: false,
        });

        return processedFiles;
      } catch (error) {
        console.error("Storage check error:", error);
        set({
          error: error.message || "Failed to check storage",
          isLoading: false,
          // Still update the current bucket with empty files
          currentBucket: { name: bucketName, files: [], user_id: userIdFilter },
          currentBucketName: bucketName,
          currentBucketFiles: [],
        });
        return [];
      } finally {
        set({ isLoading: false });
      }
    },

    /* ----------------------- */
    /* ------ BUCKETS -------- */
    /* ----------------------- */
    fetchBuckets: async () => {
      set({ isLoading: true, error: null });
      try {
        // Just use the predefined buckets - no need to query Supabase
        const buckets = SUPABASE_BUCKETS.map((name) => ({
          name,
          files: [],
        }));

        set({ buckets });
        return buckets;
      } catch (error) {
        console.error("Error setting up buckets:", error);
        // Use default buckets even if there's an error
        const buckets = SUPABASE_BUCKETS.map((name) => ({
          name,
          files: [],
        }));

        set({
          error: error.message,
          buckets,
          isLoading: false,
        });
        return buckets;
      } finally {
        set({ isLoading: false });
      }
    },

    /* ----------------------- */
    /* ------ FILES -------- */
    /* ----------------------- */
    getFileByFullPath: async (bucketName, fullPath) => {
      if (!bucketName || !fullPath) {
        console.error("Missing required parameters: bucketName or fullPath");
        return null;
      }

      try {
        // Get user ID from auth store for context
        const { useAuthStore } = await import("@/store/authStore");
        const userId =
          useAuthStore.getState().userId || useAuthStore.getState().user?.id;

        // First, try to get the file directly
        const { data, error } = await supabase.storage
          .from(bucketName)
          .download(fullPath);

        if (error) {
          console.error(
            `Error downloading file from "${bucketName}/${fullPath}":`,
            error
          );
          throw error;
        }

        // Get public URL for the file
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(fullPath);

        // Get metadata if available
        const { data: metadataData, error: metadataError } =
          await supabase.storage
            .from(bucketName)
            .getMetadata(fullPath)
            .catch((err) => {
              console.warn(`Could not get metadata for ${fullPath}:`, err);
              return { data: null, error: err };
            });

        // Create a complete file object
        const fileObject = {
          path: fullPath,
          name: fullPath.split("/").pop(),
          data,
          size: data.size,
          type: data.type,
          publicUrl: urlData?.publicUrl,
          metadata: metadataData || {},
          lastModified: data.lastModified,
          bucket: bucketName,
          user_id: userId, // Add user context
        };

        // Add to the current files for reference
        get().addCurrentFile({
          ...fileObject,
          id: `file_${Date.now()}`,
          storage_path: fullPath,
          user_id: userId,
        });

        return fileObject;
      } catch (error) {
        console.error(
          `Failed to get file from "${bucketName}/${fullPath}":`,
          error
        );

        // Try to see if we already have this file in our current files
        const cachedFile = get().currentFiles.find(
          (f) =>
            (f.bucket === bucketName && f.path === fullPath) ||
            f.storage_path === fullPath
        );

        if (cachedFile) {
          return cachedFile;
        }

        return null;
      }
    },

    /* ----------------------- */
    /* --- ASSISTANT IMAGES -- */
    /* ----------------------- */
    fetchAssistantImages: async () => {
      const bucketName = "avatars";
      const VALID_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

      try {
        set({ isLoading: true });
        const { data, error } = await supabase.storage
          .from(bucketName)
          .list("assistant/", {
            sortBy: { column: "created_at", order: "desc" },
          });

        if (error) throw error;

        // Get all image files from the assistant folder
        const imageFiles = data.filter((file) =>
          VALID_IMAGE_EXTENSIONS.some((ext) =>
            file.name.toLowerCase().endsWith(`.${ext}`)
          )
        );

        // Generate base64 data for all images
        const processedImages = await Promise.all(
          imageFiles.map(async (file) => {
            try {
              const { data: fileData, error: downloadError } =
                await supabase.storage
                  .from(bucketName)
                  .download(`assistant/${file.name}`);

              if (downloadError) throw downloadError;

              const { data: urlData } = await supabase.storage
                .from(bucketName)
                .getPublicUrl(`assistant/${file.name}`);

              // Create base64 encoding of the image
              const reader = new FileReader();
              return new Promise((resolve) => {
                reader.onloadend = () => {
                  resolve({
                    ...file,
                    path: `assistant/${file.name}`,
                    url: urlData.publicUrl,
                    base64: reader.result,
                    type: file.contentType || "image/png",
                  });
                };
                reader.readAsDataURL(fileData);
              });
            } catch (err) {
              console.error(`Error processing image ${file.name}:`, err);
              return null;
            }
          })
        );

        // Filter out any null entries and update state
        const validImages = processedImages.filter(Boolean);
        set({ assistantImages: validImages });
        return validImages;
      } catch (error) {
        console.error("Error fetching assistant images:", error);
        set({ error: error.message });
        return [];
      } finally {
        set({ isLoading: false });
      }
    },

    uploadChatFile: async (file, threadId) => {
      try {
        if (!threadId) {
          console.warn("No threadId provided for file upload");
        }

        // Get user ID from auth store
        const { useAuthStore } = await import("@/store/authStore");
        const userId =
          useAuthStore.getState().userId || useAuthStore.getState().user?.id;

        if (!userId) {
          console.error("No user ID available for file upload");
          return { error: new Error("User not authenticated") };
        }

        // Additional validation and logging

        // IMPORTANT: Define bucket name once to ensure consistency
        const bucketName = "chat";

        // Generate a more descriptive filename that clearly embeds the original name
        const timestamp = Date.now();
        const randomNum = Math.floor(Math.random() * 1000000);
        const threadPrefix = threadId
          ? `thread_${threadId.substring(0, 8)}_`
          : "";

        // Sanitize the original filename to be safe for storage paths
        const safeOriginalName = file.name
          .replace(/[^a-zA-Z0-9._-]/g, "_") // Replace unsafe chars with underscore
          .replace(/_{2,}/g, "_"); // Replace multiple underscores with single

        // Extract the file extension
        const fileExtension = file.name.includes(".")
          ? `.${file.name.split(".").pop()}`
          : "";

        // Keep extension only once (don't duplicate)
        const nameWithoutExtension = safeOriginalName.endsWith(fileExtension)
          ? safeOriginalName.slice(0, -fileExtension.length)
          : safeOriginalName;

        // Create a filename with clear separators between parts, organized by user ID
        // Format: {userId}/thread_[ID]_origname=[original-filename]_[timestamp]_[random].ext
        const fileName = `${userId}/${threadPrefix}origname=${nameWithoutExtension}_${timestamp}_${randomNum}${fileExtension}`;

        // Create file metadata - SIMPLIFIED to avoid RLS policy conflicts
        // Note: We store minimal metadata to prevent issues with storage.objects RLS
        const fileMetadata = {
          original_name: file.name,
          content_type: file.type,
          uploaded_at: new Date().toISOString(),
        };

        // Try using the native Supabase SDK - SIMPLIFIED upload without complex metadata
        try {
          const { data, error } = await supabase.storage
            .from(bucketName) // EXPLICITLY using "chat" bucket
            .upload(fileName, file, {
              cacheControl: "3600",
              upsert: true,
              contentType: file.type,
              // Removed metadata parameter to avoid RLS policy violations
              // RLS policies work with auth.uid() and owner_id, not metadata
            });

          if (error) {
            console.warn(
              `SDK upload failed (bucket=${bucketName}):`,
              error.message || JSON.stringify(error)
            );
            throw error;
          }

          // Get the public URL
          const { data: urlData } = await supabase.storage
            .from(bucketName)
            .getPublicUrl(fileName);

          // Create a complete file reference
          const fileRef = {
            id: `file-${timestamp}`,
            path: fileName,
            name: file.name,
            original_name: file.name,
            type: file.type,
            size: file.size,
            thread_id: threadId,
            user_id: userId,
            publicUrl: urlData.publicUrl,
            bucket: bucketName,
            hasVectorEmbeddings: false,
            vectorData: null,
            openai_file_id: `file-${timestamp}`,
          };

          // Store this file in the currentFiles array for later lookup
          get().addCurrentFile(fileRef);

          // Update thread context
          try {
            const { getThreadContext, setThreadContext } = (
              await import("@/store/threadStore")
            ).default.getState();

            if (threadId) {
              const currentContext = getThreadContext(threadId);
              if (currentContext) {
                const updatedContext = {
                  ...currentContext,
                  storage_files: [
                    ...(currentContext.storage_files || []),
                    fileRef,
                  ],
                };

                setThreadContext(updatedContext);
              }
            }
          } catch (err) {
            console.warn("Could not update thread context:", err);
          }

          return {
            data: {
              ...data,
              publicUrl: urlData.publicUrl,
              name: file.name,
              original_name: file.name,
              path: fileName,
              type: file.type,
              size: file.size,
              thread_id: threadId,
              user_id: userId,
              bucket: bucketName,
              id: `file-${timestamp}`,
              hasVectorEmbeddings: false,
              vectorData: null,
            },
          };
        } catch (sdkError) {
          console.warn(
            "SDK upload failed, attempting direct upload:",
            sdkError
          );
          // Continue with the rest of the existing code for the fallback approach...
        }

        // Fallback approach - direct FormData upload
        const formData = new FormData();
        formData.append("file", file);

        // Try to add metadata via FormData
        Object.entries(fileMetadata).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(`metadata[${key}]`, value);
          }
        });

        // Get the Supabase URL and key
        const supabaseUrl = supabase.supabaseUrl;
        const supabaseKey = supabase.supabaseKey;

        // Create the upload URL - IMPORTANT: Using the correct bucket name
        const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucketName}/${fileName}`;

        // Send the request directly
        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: formData,
        });

        // Check if the upload was successful
        if (!response.ok) {
          const errorData = await response.json();
          console.error("Direct upload failed:", {
            status: response.status,
            statusText: response.statusText,
            errorData,
            bucket: bucketName,
            uploadUrl,
          });

          throw new Error(
            `Upload failed: ${errorData.error || response.statusText}`
          );
        }

        // Get the data from the response
        const data = await response.json();

        // Get the public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from(bucketName).getPublicUrl(fileName);

        // Create a file reference
        const fileRef = {
          id: `file-${timestamp}`,
          path: fileName,
          name: file.name, // Original name for display
          original_name: file.name, // Extra reference
          type: file.type,
          size: file.size,
          thread_id: threadId,
          user_id: userId,
          publicUrl: publicUrl,
          metadata: fileMetadata,
          bucket: bucketName, // Track the bucket explicitly
          hasVectorEmbeddings: false,
          vectorData: null,
          openai_file_id: `file-${timestamp}`, // Placeholder, will be updated later
        };

        // Store this file in currentFiles for later lookup
        get().addCurrentFile(fileRef);

        // Update thread context if requested
        try {
          const { getThreadContext, setThreadContext } = (
            await import("@/store/threadStore")
          ).default.getState();

          if (threadId) {
            const currentContext = getThreadContext(threadId);
            if (currentContext) {
              // Update the context with the new file
              const updatedContext = {
                ...currentContext,
                storage_files: [
                  ...(currentContext.storage_files || []),
                  fileRef,
                ],
              };

              setThreadContext(updatedContext);
            }
          }
        } catch (err) {
          console.warn("Could not update thread context:", err);
        }

        // Return file info
        return {
          data: {
            ...data,
            publicUrl,
            name: file.name,
            path: fileName,
            type: file.type,
            size: file.size,
            thread_id: threadId,
            user_id: userId,
            uploaded_at: new Date().toISOString(),
            metadata: fileMetadata,
            bucket: bucketName, // Track the bucket explicitly
          },
        };
      } catch (err) {
        console.error("Unexpected error in uploadChatFile:", err);
        return { error: err };
      }
    },

    updateChatFile: async (file, messageId) => {
      const bucketName = "chat";
      if (!file) {
        set({ error: `Please select a file first` });
        return { success: false, error: "Please select a file first" };
      }

      try {
        // Get user ID from auth store
        const { useAuthStore } = await import("@/store/authStore");
        const userId =
          useAuthStore.getState().userId || useAuthStore.getState().user?.id;

        set({
          isLoading: true,
          status: `Updating file in ${bucketName}...`,
          error: null,
        });

        const { data, error: uploadError } = await supabase.storage
          .from(bucketName)
          .update(file.data.path, file.file, {
            cacheControl: "3600",
            upsert: true, // Changed from false to true to allow overwriting
            metadata: {
              message_id: messageId,
              user_id: userId,
            },
          });

        if (uploadError) throw uploadError;

        // Refresh the bucket
        await get().checkStorage(bucketName);

        set({
          status: `File updated successfully: ${file.data.path}`,
          isLoading: false,
        });

        return {
          success: true,
          data,
          error: null,
        };
      } catch (err) {
        console.error("Upload error:", err);
        set({
          error: err.message,
          status: `Update failed: ${err.message}`,
        });

        return {
          success: false,
          error: err.message,
        };
      } finally {
        set({ isLoading: false });
      }
    },
  })),
  {
    name: STORAGE_KEY,
    partialize: (state) => ({
      initialized: state.initialized,
      currentBucketName: state.currentBucketName,
      currentBucketFiles: state.currentBucketFiles,
      currentFiles: state.currentFiles,
      assistantImages: state.assistantImages,
      status: state.status,
    }),
  }
);

export default useFileStorageStore;
